import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Estado do loading do wizard
 */
export interface WizardLoadingState {
  isLoading: boolean;
  progress: number; // 0-100
  message: string;
  step: string; // Nome do step atual
}

/**
 * Step de carregamento com mensagem e peso
 */
export interface LoadingStep {
  key: string;
  message: string;
  weight: number; // Peso relativo deste step (ex: 1, 2, 3)
}

/**
 * Chaves de tradução usadas nas mensagens do wizard loading
 */
export const WizardLoadingMessages = {
  LOADING_WIZARD: 'WizardLoadingWizard',
  CHECKING_PERMISSIONS: 'WizardCheckingPermissions',
  LOADING_ACTIONS: 'WizardLoadingActions',
  CHECKING_CLAIM: 'WizardCheckingClaim',
  CHECKING_PREREQUISITES: 'WizardCheckingPrerequisites',
  CALCULATING_PROGRESS: 'WizardCalculatingProgress',
  PROCESSING: 'WizardProcessing',
  SAVING_PROGRESS: 'WizardSavingProgress',
  CHECKING_CLAIM_STATUS: 'WizardCheckingClaimStatus',
  CALCULATING_NEXT_STEP: 'WizardCalculatingNextStep',
  NEXT_STEP: 'WizardNextStep',
  DONE: 'WizardDone',
} as const;

/**
 * Service para gerenciar loading durante transições de steps do wizard
 * Mostra progresso determinado com mensagens personalizadas
 */
@Injectable({
  providedIn: 'root',
})
export class WizardLoadingService {
  private loadingState$ = new BehaviorSubject<WizardLoadingState>({
    isLoading: false,
    progress: 0,
    message: '',
    step: '',
  });

  private currentSteps: LoadingStep[] = [];
  private completedSteps: Set<string> = new Set();
  private totalWeight = 0;

  /**
   * Observable para componentes se inscreverem
   */
  getLoadingState(): Observable<WizardLoadingState> {
    return this.loadingState$.asObservable();
  }

  /**
   * Obtém o estado atual do loading
   */
  getCurrentState(): WizardLoadingState {
    return this.loadingState$.value;
  }

  /**
   * Inicia um processo de loading com steps definidos
   *
   * @param steps - Array de steps com mensagens e pesos
   * @param initialMessage - Mensagem inicial (opcional)
   *
   * @example
   * ```ts
   * loadingService.start([
   *   { key: 'claim', message: 'Verificando claim...', weight: 1 },
   *   { key: 'actions', message: 'Carregando actions...', weight: 2 },
   *   { key: 'state', message: 'Calculando próximo passo...', weight: 1 }
   * ]);
   * ```
   */
  start(steps: LoadingStep[], initialMessage: string = 'Iniciando...'): void {
    console.log('[WIZARD_LOADING] Starting with steps', {
      totalSteps: steps.length,
      steps: steps.map(s => ({ key: s.key, message: s.message, weight: s.weight })),
    });

    this.currentSteps = steps;
    this.completedSteps.clear();
    this.totalWeight = steps.reduce((sum, step) => sum + step.weight, 0);

    this.loadingState$.next({
      isLoading: true,
      progress: 0,
      message: initialMessage,
      step: '',
    });
  }

  /**
   * Marca um step como completo e atualiza o progresso
   *
   * @param stepKey - Chave do step que foi completado
   * @param nextMessage - Mensagem para o próximo passo (opcional)
   */
  completeStep(stepKey: string, nextMessage?: string): void {
    if (this.completedSteps.has(stepKey)) {
      console.warn('[WIZARD_LOADING] Step already completed', { stepKey });
      return;
    }

    this.completedSteps.add(stepKey);

    // Calcula progresso baseado no peso dos steps completados
    const completedWeight = this.currentSteps
      .filter(step => this.completedSteps.has(step.key))
      .reduce((sum, step) => sum + step.weight, 0);

    const progress = this.totalWeight > 0
      ? Math.round((completedWeight / this.totalWeight) * 100)
      : 0;

    // Encontra o próximo passo pendente
    const nextStep = this.currentSteps.find(
      step => !this.completedSteps.has(step.key)
    );

    const message = nextMessage || nextStep?.message || WizardLoadingMessages.DONE;

    console.log('[WIZARD_LOADING] Step completed', {
      completedStep: stepKey,
      progress,
      nextStep: nextStep?.key,
      message,
    });

    this.loadingState$.next({
      isLoading: true,
      progress,
      message,
      step: nextStep?.key || '',
    });
  }

  /**
   * Atualiza apenas a mensagem sem mudar o progresso
   * Útil para dar feedback durante um step longo
   */
  updateMessage(message: string): void {
    const currentState = this.loadingState$.value;

    console.log('[WIZARD_LOADING] Updating message', {
      oldMessage: currentState.message,
      newMessage: message,
    });

    this.loadingState$.next({
      ...currentState,
      message,
    });
  }

  /**
   * Atualiza o progresso manualmente (0-100)
   * Use com cautela - preferir completeStep() quando possível
   */
  setProgress(progress: number, message?: string): void {
    const currentState = this.loadingState$.value;
    const clampedProgress = Math.max(0, Math.min(100, progress));

    console.log('[WIZARD_LOADING] Setting progress', {
      progress: clampedProgress,
      message,
    });

    this.loadingState$.next({
      ...currentState,
      progress: clampedProgress,
      message: message || currentState.message,
    });
  }

  /**
   * Finaliza o loading (progresso 100%)
   *
   * @param finalMessage - Mensagem final (opcional)
   * @param hideDelay - Delay em ms antes de esconder (opcional, padrão 300ms)
   */
  finish(finalMessage: string = 'Concluído!', hideDelay: number = 300): void {
    console.log('[WIZARD_LOADING] Finishing', {
      finalMessage,
      hideDelay,
    });

    this.loadingState$.next({
      isLoading: true,
      progress: 100,
      message: finalMessage,
      step: '',
    });

    // Esconde após delay
    setTimeout(() => {
      this.hide();
    }, hideDelay);
  }

  /**
   * Esconde o loading imediatamente
   */
  hide(): void {
    console.log('[WIZARD_LOADING] Hiding');

    this.loadingState$.next({
      isLoading: false,
      progress: 0,
      message: '',
      step: '',
    });

    this.currentSteps = [];
    this.completedSteps.clear();
    this.totalWeight = 0;
  }

  /**
   * Verifica se está carregando
   */
  isLoading(): boolean {
    return this.loadingState$.value.isLoading;
  }

  /**
   * Helper para criar steps comuns de carregamento
   */
  static readonly CommonSteps = {
    /**
     * Steps para carregamento inicial do wizard
     */
    INIT: [
      { key: 'guard', message: WizardLoadingMessages.CHECKING_PERMISSIONS, weight: 1 },
      { key: 'claim', message: WizardLoadingMessages.CHECKING_CLAIM, weight: 1 },
      { key: 'actions', message: WizardLoadingMessages.LOADING_ACTIONS, weight: 2 },
      { key: 'prerequisites', message: WizardLoadingMessages.CHECKING_PREREQUISITES, weight: 1 },
      { key: 'state', message: WizardLoadingMessages.CALCULATING_PROGRESS, weight: 2 },
    ],

    /**
     * Steps para transição entre actions (between steps)
     */
    BETWEEN_STEPS: [
      { key: 'reload', message: WizardLoadingMessages.SAVING_PROGRESS, weight: 1 },
      { key: 'verify_claim', message: WizardLoadingMessages.CHECKING_CLAIM_STATUS, weight: 1 },
      { key: 'calculate_next', message: WizardLoadingMessages.CALCULATING_NEXT_STEP, weight: 2 },
    ],

    /**
     * Steps para completar questionário
     * UNUSED - This step is not used in the guide
     */
    COMPLETE_QUESTIONNAIRE: [
      { key: 'save', message: 'Salvando respostas...', weight: 3 },
      { key: 'validate', message: 'Validando questionário...', weight: 1 },
      { key: 'next', message: 'Preparando próximo passo...', weight: 1 },
    ],

    /**
     * Steps para completar KYC
     * UNUSED - This step is not used in the guide
     */
    COMPLETE_KYC: [
      { key: 'upload', message: 'Enviando documentos...', weight: 3 },
      { key: 'verify', message: 'Verificando identidade...', weight: 2 },
      { key: 'next', message: 'Preparando próximo passo...', weight: 1 },
    ],

    /**
     * Steps para completar assinatura
     * UNUSED - This step is not used in the guide
     */
    COMPLETE_SIGNATURE: [
      { key: 'sign', message: 'Processando assinatura...', weight: 3 },
      { key: 'save', message: 'Salvando contrato assinado...', weight: 1 },
      { key: 'next', message: 'Preparando próximo passo...', weight: 1 },
    ],
  };
}
